import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DURATION, EASE_OUT_SOFT, useReducedMotion } from "../lib/motion";

/**
 * Beyond Sure — Multilingual Welcome cycle (v3.4).
 *
 * Replaces the static "Welcome." heading on the desktop RIGHT form panel
 * with a heading that crossfades through 12 Indian languages + English on
 * a ~2.2s loop:
 *
 *   English · Hindi · Bengali · Tamil · Telugu · Marathi · Gujarati ·
 *   Kannada · Malayalam · Punjabi · Odia · Assamese.
 *
 * Critical typography note: Hanken Grotesk (the brand display face) does
 * NOT contain Indic glyphs. If we tried to render "नमस्ते" in Hanken alone
 * the browser would draw tofu boxes (□□□). So this component uses a
 * dedicated Indic-capable stack (var(--font-multilingual)) — Hanken first
 * for English, then per-script Noto Sans cuts for every Unicode block we
 * cycle through. The browser picks the right per-glyph automatically.
 *
 * Reduced-motion: when the user prefers reduced motion, render the
 * static English "Welcome." and skip the cycle entirely. No FOMO — the
 * form heading still reads, and motion preference is respected.
 *
 * The component is layout-friendly: it claims a stable min-height so the
 * form below doesn't jump as longer phrases (e.g. "ਜੀ ਆਇਆਂ ਨੂੰ") replace
 * shorter ones.
 */

interface Greeting {
  lang: string;            // human-readable language label, for aria + debug
  code: string;            // BCP47 lang code for the rendered <span>
  text: string;            // the greeting itself
}

// Order roughly matches the user's v5 brief; English leads so the FIRST
// frame is the recognised hook.
const GREETINGS: Greeting[] = [
  { lang: "English",   code: "en",    text: "Welcome." },
  { lang: "Hindi",     code: "hi",    text: "नमस्ते"     },
  { lang: "Bengali",   code: "bn",    text: "স্বাগতম"    },
  { lang: "Tamil",     code: "ta",    text: "வணக்கம்"     },
  { lang: "Telugu",    code: "te",    text: "స్వాగతం"    },
  { lang: "Marathi",   code: "mr",    text: "स्वागत"     },
  { lang: "Gujarati",  code: "gu",    text: "સ્વાગત"     },
  { lang: "Kannada",   code: "kn",    text: "ಸ್ವಾಗತ"     },
  { lang: "Malayalam", code: "ml",    text: "സ്വാഗതം"    },
  { lang: "Punjabi",   code: "pa",    text: "ਜੀ ਆਇਆਂ ਨੂੰ" },
  { lang: "Odia",      code: "or",    text: "ସ୍ୱାଗତ"     },
  { lang: "Assamese",  code: "as",    text: "স্বাগতম"    },
];

const TICK_MS = 2200;

interface Props {
  // Optional className passed by the parent so we can tune the size at the
  // call site without forcing per-screen overrides into this file.
  className?: string;
}

export function WelcomeCycle({ className = "" }: Props) {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % GREETINGS.length);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [reduced]);

  // When reduced motion is on, render the static English greeting and exit.
  // Wrapping in the same shell keeps the surrounding layout stable.
  if (reduced) {
    return (
      <h2
        lang="en"
        className={[
          "bsure-multilingual leading-[1.05]",
          className,
        ].join(" ")}
        style={{ fontWeight: 900 }}
      >
        Welcome.
      </h2>
    );
  }

  const current = GREETINGS[index];

  return (
    <h2
      aria-live="polite"
      aria-label="Welcome — cycling across Indian languages"
      className={[
        // min-h holds the layout steady so the form below doesn't shift.
        "relative inline-flex items-center min-h-[1.2em] leading-[1.05]",
        className,
      ].join(" ")}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={current.code}
          lang={current.code}
          initial={{ opacity: 0, y: 8, filter: "blur(2px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -8, filter: "blur(2px)" }}
          transition={{ duration: DURATION.slow, ease: EASE_OUT_SOFT }}
          className="bsure-multilingual inline-block"
          style={{ fontWeight: 900 }}
        >
          {current.text}
        </motion.span>
      </AnimatePresence>
    </h2>
  );
}

export default WelcomeCycle;
